/**
 * Svelte use: action for long-press (touch hold) detection.
 * Ported from public/binder.js (lines 1491-1563).
 *
 * Usage:
 *   <div use:longpress={{ onLongPress: (e) => { ... }, duration: 600, maxMove: 15 }}>
 *
 * @param {HTMLElement} node
 * @param {{ onLongPress?: (detail: { node: HTMLElement, event: TouchEvent }) => void, duration?: number, maxMove?: number }} options
 */
export function longpress(node, options = {}) {
  let { onLongPress, duration = 600, maxMove = 15 } = options;

  let timer = null;
  let startX = 0;
  let startY = 0;
  let fired = false;

  function handleTouchStart(e) {
    if (e.touches.length !== 1) return;

    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    fired = false;

    clearTimeout(timer);
    timer = setTimeout(() => {
      fired = true;
      if (navigator.vibrate) navigator.vibrate(50);
      onLongPress?.({ node, event: e });
    }, duration);
  }

  function handleTouchMove(e) {
    if (timer === null || e.touches.length !== 1) return;

    const deltaX = Math.abs(e.touches[0].clientX - startX);
    const deltaY = Math.abs(e.touches[0].clientY - startY);

    if (deltaX > maxMove || deltaY > maxMove) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function handleTouchEnd(e) {
    clearTimeout(timer);
    timer = null;

    // Prevent the tap/click from firing if long-press just triggered
    if (fired) {
      e.preventDefault();
      fired = false;
    }
  }

  function handleTouchCancel() {
    clearTimeout(timer);
    timer = null;
  }

  node.addEventListener('touchstart', handleTouchStart, { passive: true });
  node.addEventListener('touchmove', handleTouchMove, { passive: true });
  node.addEventListener('touchend', handleTouchEnd);
  node.addEventListener('touchcancel', handleTouchCancel);

  return {
    update(newOptions) {
      onLongPress = newOptions.onLongPress;
      if (newOptions.duration != null) duration = newOptions.duration;
      if (newOptions.maxMove != null) maxMove = newOptions.maxMove;
    },
    destroy() {
      clearTimeout(timer);
      node.removeEventListener('touchstart', handleTouchStart);
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchend', handleTouchEnd);
      node.removeEventListener('touchcancel', handleTouchCancel);
    },
  };
}
